import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase.js'

// Pulls the active restaurant's AI spend for a rolling window via the
// `ai_usage_for_restaurant` Postgres function. The RPC returns:
//   {
//     total_calls:      bigint,
//     total_input:      bigint,
//     total_output:     bigint,
//     total_cost_cents: bigint,
//     by_feature: [{ feature, calls, cost_cents }, ...]
//   }
//
// Only `success` rows count toward the totals (errors don't bill the
// customer). Used by the AiUsageCard on Settings — gives the manager
// a quick "what is the AI doing for me, and what's it costing" panel.
//
// Window defaults to 30 days. Pass {days} to override.
export function useAiUsage({ days = 30 } = {}) {
  const [data, setData] = useState(null)
  const [restaurantId, setRestaurantId] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const refetch = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // Same active-restaurant pattern as useSubscription. Pilot
      // accounts have one active restaurant; multi-location comes later.
      const { data: rest, error: restErr } = await supabase
        .from('restaurants')
        .select('id')
        .eq('active', true)
        .order('created_at')
        .limit(1)
        .maybeSingle()
      if (restErr) throw restErr
      if (!rest) {
        setData(null)
        setRestaurantId(null)
        return
      }
      setRestaurantId(rest.id)

      const since = new Date()
      since.setDate(since.getDate() - days)
      const { data: rows, error } = await supabase.rpc('ai_usage_for_restaurant', {
        p_restaurant_id: rest.id,
        p_since: since.toISOString(),
      })
      if (error) throw error
      const row = Array.isArray(rows) ? rows[0] : rows
      setData(row || {
        total_calls: 0,
        total_input: 0,
        total_output: 0,
        total_cost_cents: 0,
        by_feature: [],
      })
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useAiUsage] failed', e)
      setError(e.message || String(e))
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [days])

  useEffect(() => { refetch() }, [refetch])

  return { data, loading, error, restaurantId, refetch }
}

// Map machine feature keys to human labels for the UI.
export const FEATURE_LABELS = {
  'huddle-brief':       'Daily brief',
  'weekly-insights':    'Weekly insights',
  'recipe-build':       'Recipe builder',
  'reorder-suggest':    'Smart reorder',
  'receive-anomalies':  'Receive scanner',
  'review-reply':       'Review replies',
  'promo-posts':        'Promo posts',
  'marketing-drafts':   'Marketing drafts',
  'invoice-extract':    'Invoice scanner',
  'document-expiration':'Doc scanner',
  'schedule-suggest':   'Schedule optimizer',
}

export function labelForFeature(key) {
  return FEATURE_LABELS[key] || key
}
